/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';

/**
 * Debounces the call to change the state variable
 * to allow the value to change multiple times without
 * actually affecting the state.
 *
 * @param initialValue The initial value to start the state with.
 * @param delay The debounce delay.
 */
export const useDebouncedState = <T>(initialValue: T, delay: number): [T, (value: T) => void] => {
    const [state, setState] = React.useState<T>(initialValue);
    let timeoutHandler: NodeJS.Timeout;

    const setDebouncedState = (value: T) => {
        if (timeoutHandler) {
            clearTimeout(timeoutHandler);
        }

        timeoutHandler = setTimeout(() => setState(value), delay);
    };

    return [state, setDebouncedState];
};
